import { useEffect, useState, forwardRef } from 'react';
import PropTypes from 'prop-types';

// Single card surface for the whole app (Tailwind only).
// Components that bleed images to the card edge must mirror this scale
// with negative margins (see BLEED maps in Card/SimpleCard).
const PADDINGS = {
  none: 'p-0',
  xs: 'p-2.5',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

// Tabs always bleed to the card edges, whatever the padding.
const TAB_BLEEDS = {
  none: '',
  xs: '-mx-2.5 -mt-2.5',
  sm: '-mx-4 -mt-4',
  md: '-mx-6 -mt-6',
  lg: '-mx-8 -mt-8',
};

const Box = forwardRef((props, ref) => {
  const {
    className = '',
    children,
    tabs = null,
    padding = 'md',
    onChangeTab = () => {},
    ...rest
  } = props;

  const [activeTab, setActiveTab] = useState(
    tabs?.length
      ? tabs?.find((tab) => tab.active)?.value || tabs[0]?.value
      : null,
  );

  useEffect(() => {
    if (activeTab) {
      onChangeTab(activeTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const isSelected = /\bselected\b/.test(className);

  return (
    <div
      ref={ref}
      className={`box bg-white shadow-card rounded-card ${PADDINGS[padding] ?? PADDINGS.md} ${isSelected ? 'border-2 border-primary transition-colors' : ''} ${className}`}
      {...rest}
    >
      {tabs?.length && (
        <div className={`flex justify-between items-stretch mb-4 ${TAB_BLEEDS[padding] ?? TAB_BLEEDS.md}`}>
          {tabs.map((tab) => (
            <div
              key={tab.value}
              className={`flex-1 flex items-center justify-center px-6 py-2.5 text-center font-bold text-sm leading-none cursor-pointer border-b-2 ${activeTab === tab.value ? 'border-primary' : 'border-neutral-300'}`}
              onClick={() => setActiveTab(tab.value)}
            >
              {tab.label}
            </div>
          ))}
        </div>
      )}
      <div className='box-body w-full'>
        {children}
      </div>
    </div>
  );
});

Box.displayName = 'Box';

Box.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  tabs: PropTypes.array,
  onChangeTab: PropTypes.func,
  padding: PropTypes.oneOf(['none', 'xs', 'sm', 'md', 'lg']),
};

export default Box;
